import { useCallback, useEffect, useMemo } from 'react';
import {
  isAgentAction,
  isCodingAgentAction,
  isFoundryAgentAction,
  isFoundryAgentModelType,
  isMcpAgentTool,
} from '@/types/workflowDefinition';
import type {
  Action,
  AgentTool,
  Trigger,
} from '@/types/workflowDefinition';
import { readAgentModelType } from '../../workflowHelpers';
import {
  useNodeEditorStore,
  buildNodeEditorDefinition,
  resolveEditorDefinition,
} from '@/stores/nodeEditorStore';
import { useWorkflowStore } from '@/stores/workflowStore';
import { useConnectionStore } from '@/stores/connectionStore';
import { useNodeSettings } from '@/hooks/useNodeSettings';
import {
  cleanAction,
  cleanOperation,
} from '@/services/workflowSerializer';
import { getBuiltInManifest } from '@/data/manifests';
import { requiresConnection } from '@/types/operationManifest';
import { operationRequiresConnection } from '@/types/connectionValidation';
import type { OperationInfo } from '@/types/operationManifest';
import type { NodeSettings } from '@/types/nodeSettings';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';

/**
 * The full set of node-editor tabs. This is the union used by both the legacy
 * three-panel `ExpandedView` and the new drawer IA. The visible subset is
 * derived per node via {@link useNodeEditorContext}'s `availableTabs`.
 *
 * `advanced-parameters` is a drawer-only tab — it hosts the advanced-param
 * picker + inline fields in the peer Advanced section. It is intentionally
 * excluded from {@link TAB_CONFIG} so it does not appear as an extra strip in
 * the legacy `ExpandedView` (where Advanced still lives at the tail of the
 * Parameters tab).
 */
export type EditorTab =
  | 'parameters'
  | 'connection'
  | 'settings'
  | 'agent-harness'
  | 'knowledge'
  | 'tool'
  | 'mcp-config'
  | 'mock'
  | 'code'
  | 'about'
  | 'advanced-parameters';

/**
 * Tab configuration with labels. Order matters for the legacy tab strip.
 *
 * `connection` leads: for connector nodes it renders as the first tab so the
 * author's first step is picking a connection before Parameters becomes
 * useful. Non-connector nodes filter it out via `availableTabs`, so Parameters
 * naturally becomes the first visible tab there.
 */
export const TAB_CONFIG: { id: EditorTab; label: string }[] = [
  { id: 'connection', label: 'Connection' },
  { id: 'parameters', label: 'Parameters' },
  { id: 'tool', label: 'Tool' },
  { id: 'mcp-config', label: 'MCP server' },
  { id: 'agent-harness', label: 'Harness' },
  { id: 'knowledge', label: 'Knowledge' },
  { id: 'settings', label: 'Run settings' },
  { id: 'mock', label: 'Test data' },
  { id: 'code', label: 'Code' },
  { id: 'about', label: 'About' },
];

/**
 * Determine if this node type should have a Connection tab.
 * This includes both standard single-slot and Agent multi-slot connections.
 * Checks the manifest's connection.required property first, then falls back to
 * type-based check.
 */
export function needsConnectionTab(nodeType: string | undefined): boolean {
  if (!nodeType) return false;

  // Check manifest first - it's the source of truth for connection requirements
  const manifest = getBuiltInManifest(nodeType);
  if (manifest?.properties.connection !== undefined) {
    return requiresConnection(manifest);
  }

  // Fall back to type-based check for operations without manifests
  return operationRequiresConnection(nodeType);
}

/**
 * Everything the node-editor tab bodies need, derived once from the store.
 * Shared by the legacy `ExpandedView` and the new `NodeEditorDrawer` so the
 * tab-rendering logic lives in exactly one place ({@link NodeEditorTabBody}).
 */
export interface NodeEditorContextValue {
  node: ReturnType<typeof useNodeEditorStore.getState>['node'];
  nodeId: string | null;
  nodeType: string | undefined;
  parameterValues: Record<string, unknown>;

  // Variant flags
  isAgent: boolean;
  isHostedAgent: boolean;
  isFoundryAgent: boolean;
  isCodingAgent: boolean;
  isTool: boolean;
  isMcpTool: boolean;
  isMockedNode: boolean;
  showConnectionTab: boolean;

  availableTabs: { id: EditorTab; label: string }[];
  connectionName: string | undefined;
  toolData: AgentTool | undefined;
  liveNodeData: Record<string, unknown> | null;
  liveNodeError: string | null;
  agentConnectionsConfig:
    | Record<string, { endpoint?: string; resourceId?: string }>
    | undefined;

  // Settings bundle
  settings: NodeSettings;
  onSettingsChange: (settings: NodeSettings) => void;
  settingsValidationErrors: ReturnType<
    typeof useNodeEditorStore.getState
  >['settingsValidationErrors'];
  isTrigger: boolean;
  settingsLoading: boolean;
  settingsError: string | null;
  availablePredecessors: ReturnType<typeof useNodeSettings>['availablePredecessors'];
  isReadOnly: boolean;
}

/**
 * Compute the node-editor tab context from the current store state. This is a
 * faithful extraction of the derivations that previously lived inline in
 * `ExpandedView`, so behavior is identical — it is simply reusable now.
 *
 * Call this ONCE per open editor shell (the legacy `ExpandedView` or the new
 * drawer's section host). It owns the settings-initialization side effect, so
 * calling it in two live shells at once would double-initialize.
 */
export function useNodeEditorContext(): NodeEditorContextValue {
  // Whole-store read matches the legacy ExpandedView behavior exactly; the
  // node editor re-renders on parameter/settings changes by design.
  const {
    node,
    parameterValues,
    originalValues,
    settingsValues,
    originalSettings,
    settingsValidationErrors,
    setSettings,
    initializeSettings,
  } = useNodeEditorStore();

  const isReadOnly = useNodeEditorStore((state) => state.isReadOnly);
  const parameterDefinitions = useNodeEditorStore(
    (state) => state.parameterDefinitions,
  );
  const swaggerOperationMetadata = useNodeEditorStore(
    (state) => state.swaggerOperationMetadata,
  );
  const pickerDisplayValues = useNodeEditorStore(
    (state) => state.pickerDisplayValues,
  );

  const nodeId = node?.id ?? null;
  const nodeType = node?.nodeData?.type;

  const mockedNode = useWorkflowStore((state) =>
    nodeId ? state.mockedNodes[nodeId] : undefined,
  );
  const isMockedNode = !!mockedNode;
  const workflow = useWorkflowStore((state) => state.workflow);

  const agentConnectionsConfig = useConnectionStore(
    (state) => state.originalConnectionsConfig?.agentConnections,
  );

  // Derive operation info from parameter values (feeds useNodeSettings)
  const operationInfo = useMemo<OperationInfo | null>(() => {
    const apiId = parameterValues['_apiId'] as string | undefined;
    const operationId = parameterValues['_operationId'] as string | undefined;
    if (!apiId || !operationId) return null;
    return { connectorId: apiId, operationId };
  }, [parameterValues]);

  const {
    settings: manifestSettings,
    isTrigger,
    isLoading: settingsLoading,
    error: settingsError,
    availablePredecessors,
  } = useNodeSettings(nodeId, operationInfo, nodeType, node?.nodeData);

  // Initialize settings from manifest when available (only if not already
  // initialized). Owned here so both shells share one init path.
  useEffect(() => {
    if (manifestSettings && !settingsValues) {
      initializeSettings(manifestSettings);
    }
  }, [manifestSettings, settingsValues, initializeSettings]);

  const onSettingsChange = useCallback(
    (newSettings: NodeSettings) => {
      setSettings(newSettings);
    },
    [setSettings],
  );

  // Variant derivations
  const isAgent = !!(
    node?.type === 'action' &&
    node?.nodeData &&
    isAgentAction(node.nodeData as Action)
  );
  const isHostedAgent =
    isAgent &&
    (parameterValues['agentModelType'] === 'FoundryHostedAgent' ||
      parameterValues['parameters.agentModelType'] === 'FoundryHostedAgent');
  const isFoundryAgent =
    !!(
      node?.type === 'action' &&
      node?.nodeData &&
      isFoundryAgentAction(node.nodeData as Action)
    ) ||
    (!isHostedAgent &&
      isAgent &&
      isFoundryAgentModelType(readAgentModelType(parameterValues)));
  // Managed Agent — base detection uses the action's serialized harness type,
  // but also honor the live AgentHarnessSelector value so a freshly seeded
  // node (where the tab is the source of truth) keeps the tab visible.
  const isCodingAgent =
    !!(
      node?.type === 'action' &&
      node?.nodeData &&
      isCodingAgentAction(node.nodeData as Action)
    ) ||
    (isAgent &&
      typeof parameterValues['agentModelSettings.agentHarness.type'] ===
        'string');
  const isTool = node?.type === 'tool';
  const isMcpTool = isTool && isMcpAgentTool(node?.toolData);
  // Hosted agents no longer use a dedicated Connection tab — their agent
  // selection experience is surfaced inside the Parameters tab instead (see
  // NodeEditorTabBody, issue #2732). `&& !isHostedAgent` is a strict no-op for
  // every other node type, so native/Foundry/managed agents, tools and
  // connection operations keep their existing Connection-tab behavior.
  const showConnectionTab = needsConnectionTab(nodeType) && !isHostedAgent;

  const isKnowledgeManagementEnabled = useFeatureFlag('knowledgeManagement');

  const availableTabs = useMemo(() => {
    // MCP tools have no parameters / settings / connection / inner-action;
    // surface only the MCP server config and the About details tab.
    if (isMcpTool) {
      return TAB_CONFIG.filter(
        (tab) => tab.id === 'mcp-config' || tab.id === 'about',
      );
    }
    return TAB_CONFIG.filter((tab) => {
      if (tab.id === 'connection') return showConnectionTab;
      if (tab.id === 'mock') return isMockedNode;
      if (tab.id === 'knowledge')
        return isAgent && !isHostedAgent && isKnowledgeManagementEnabled;
      if (tab.id === 'tool') return isTool;
      if (tab.id === 'mcp-config') return false; // non-MCP tools never show this
      // Hosted agents no longer use the shared Harness tab — their platform
      // configuration is folded into the Parameters tab's "Hosted Agent
      // Configuration" section (see HostedAgentConfigPanel). Coding/Foundry
      // keep the Harness tab.
      if (tab.id === 'agent-harness')
        return isCodingAgent || isFoundryAgent;
      return true;
    });
  }, [
    showConnectionTab,
    isMockedNode,
    isAgent,
    isHostedAgent,
    isCodingAgent,
    isTool,
    isMcpTool,
    isKnowledgeManagementEnabled,
  ]);

  // Build live preview of node data reflecting current parameter and settings
  // changes. This shows users what will be saved without closing/reopening.
  const currentDefinition = useMemo(
    () => node && workflow ? resolveEditorDefinition(node, workflow) : null,
    [node, workflow],
  );
  const liveDefinition = useMemo(() => {
    if (!node?.nodeData) return null;
    if (!currentDefinition) {
      return { success: false as const, error: 'This node changed or was removed. Reopen its latest definition.' };
    }
    return buildNodeEditorDefinition({
      node, parameterValues, originalValues, settingsValues, originalSettings,
      parameterDefinitions, swaggerOperationMetadata, pickerDisplayValues,
    }, currentDefinition, workflow?.kind);
  }, [
    node,
    parameterValues,
    originalValues,
    settingsValues,
    originalSettings,
    parameterDefinitions,
    swaggerOperationMetadata,
    pickerDisplayValues,
    currentDefinition,
    workflow,
  ]);
  const previewDefinition = liveDefinition?.success ? liveDefinition.value : currentDefinition;
  const liveNodeData = previewDefinition
    ? (node?.type === 'trigger'
      ? cleanOperation(previewDefinition as Trigger, false)
      : cleanAction(previewDefinition as Action, false)) as Record<string, unknown>
    : null;
  const liveNodeError = liveDefinition && !liveDefinition.success ? liveDefinition.error : null;

  // For Agent nodes, connection is stored in modelConfigurations.model1, not in
  // host.connection.
  const connectionName = isAgent
    ? (parameterValues['_modelConnection_model1'] as string | undefined)
    : (parameterValues['_connection'] as string | undefined);

  // toolData is stored separately in node.toolData (not derived from nodeData).
  const toolData: AgentTool | undefined =
    isTool && node?.toolData ? node.toolData : undefined;

  return {
    node,
    nodeId,
    nodeType,
    parameterValues,
    isAgent,
    isHostedAgent,
    isFoundryAgent,
    isCodingAgent,
    isTool,
    isMcpTool,
    isMockedNode,
    showConnectionTab,
    availableTabs,
    connectionName,
    toolData,
    liveNodeData,
    liveNodeError,
    agentConnectionsConfig,
    settings: settingsValues ?? manifestSettings ?? ({} as NodeSettings),
    onSettingsChange,
    settingsValidationErrors,
    isTrigger,
    settingsLoading,
    settingsError,
    availablePredecessors,
    isReadOnly,
  };
}
